import {
  arrayOf, bool, element, func, number, oneOf, shape, string
} from 'prop-types';
import { getDomPath } from '@thd-nucleus/experience-context';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import {
  CHECK_NEARBY_STORE,
  COMPARE,
  PRODUCT_DETAILS,
  PRODUCT_IMAGE,
  PRODUCT_POD_CLICK,
  PRODUCT_POD_COMPARE, PRODUCT_POD_SIMILAR,
  RATING
} from './constants';
import { publish, publishAnalyticsEvent } from '../analytics';
import {
  checkScheduleAMeasureEligibility,
  checkIsHDQCSku,
  isCustomKitchenCabinetProduct,
  isBrioProduct,
} from './utils';

export const ProductPodPropTypes = {
  analyticsData: shape({}),
  brandMaxLine: number,
  channelFromTest: string,
  className: string,
  comparable: bool,
  // Prevents showing "Choose Your Options" button instead of ATC when product is generic
  doNotShowChooseYourOptions: bool,
  dpdNewTab: bool,
  hasInStoreFilter: bool,
  hideATC: bool,
  hideFavorites: bool,
  hideFulfillment: bool,
  hideLimitPerOrder: bool,
  hidePriceBadge: bool,
  hidePromotions: bool,
  hideRebates: bool,
  hideSavingsText: bool,
  hideSeeSimilarItems: bool,
  hideShippingInfo: bool,
  hideShopThisCollection: bool,
  hideStoreInfo: bool,
  hover: bool,
  hoverDelay: number,
  installationServices: bool,
  isEtchOne: bool,
  // this prop is deprecated, migrate to the anlyticsData prop
  irgSectionName: string,
  itemId: string,
  keyword: string,
  maxKeyFeatures: number,
  miniOptions: shape({
    disabled: bool,
    onAction: func,
    enableATC: bool,
    silentATC: bool,
    onlyShowIfFulfillable: bool,
    inCartQuantity: number,
    callToActionSection: element
  }),
  mobilePodType: oneOf(['singleColumn', 'doubleColumn']),
  noATCFulfillment: bool,
  onCartFailure: func,
  onCartSuccess: func,
  onClick: func,
  onCompare: func,
  onError: func,
  onFavoriteChange: func,
  onRemove: func,
  onHover: func,
  onHoverExit: func,
  onHoverSuperSku: func,
  onSelectedForCompare: func,
  onShowSimilarClick: func,
  onSuperSkuChange: func,
  pageNumber: number,
  pageType: string,
  podCount: number,
  podIndex: number,
  podType: oneOf(['grouped', 'horizontal', 'sidebyside', 'default', 'mini', 'list']),
  position: number,
  preFetchProductData: bool,
  recommendationVersion: string,
  removable: bool,
  selectedForCompare: arrayOf(string),
  showAisleAndBay: bool,
  showDpdPod: bool,
  showExactPriceOnLoad: bool,
  showFavoritesCount: bool,
  showModelNumber: bool,
  showPreferredPricingBadge: bool,
  showPreferredPricingBadgeToolTip: bool,
  showSample: bool,
  showSeeMoreOptions: bool,
  showShippingInfoInHover: bool,
  showSimilarItem: bool,
  showStoreInfo: bool,
  showSSKUMiniSwatches: bool,
  showDisabledATC: bool,
  showQuantityBox: bool,
  stfTopRight: bool,
  storeId: string,
  // number of swatches to display when using the mini swatches (see ProductDefault)
  swatchCount: number,
  target: string,
  testId: string,
  titleMaxLine: number,
  zipCode: string,
  isBOLDCategory: bool,
  showKPF: bool,
  ignoreAnalytics: bool,
  // this prop is deprecated, migrate to the anlyticsData prop
  sharedSection: string,
  hideCompareBox: bool
};

export const ProductPodDefaultProps = {
  analyticsData: null,
  brandMaxLine: null,
  channelFromTest: null,
  className: null,
  comparable: false,
  dpdNewTab: false,
  doNotShowChooseYourOptions: false,
  hasInStoreFilter: false,
  hideATC: false,
  hideFavorites: false,
  hideFulfillment: false,
  hideLimitPerOrder: false,
  hidePriceBadge: false,
  hidePromotions: false,
  hideRebates: false,
  hideSavingsText: false,
  hideSeeSimilarItems: false,
  hideShippingInfo: false,
  hideShopThisCollection: false,
  hideStoreInfo: false,
  hover: false,
  hoverDelay: 200,
  installationServices: false,
  isEtchOne: false,
  irgSectionName: '',
  itemId: '',
  keyword: null,
  maxKeyFeatures: 0,
  miniOptions: {
    disabled: false,
    onAction: () => {},
    enableATC: true,
    silentATC: false,
    onlyShowIfFulfillable: false,
    inCartQuantity: 0,
    callToActionSection: null
  },
  mobilePodType: 'doubleColumn',
  noATCFulfillment: false,
  onCartFailure: () => {},
  onCartSuccess: () => {},
  onClick: null,
  onCompare: () => {},
  onError: null,
  onFavoriteChange: null,
  onHover: null,
  onHoverExit: null,
  onHoverSuperSku: null,
  onRemove: () => {},
  onSelectedForCompare: () => {},
  onShowSimilarClick: null,
  onSuperSkuChange: null,
  pageNumber: 1,
  pageType: 'browse',
  podCount: null,
  podIndex: 1,
  podType: 'default',
  position: 1,
  preFetchProductData: false,
  recommendationVersion: 'n/a',
  removable: false,
  selectedForCompare: [],
  showAisleAndBay: false,
  showDisabledATC: false,
  showDpdPod: false,
  showExactPriceOnLoad: false,
  showFavoritesCount: false,
  showModelNumber: false,
  showPreferredPricingBadge: false,
  showPreferredPricingBadgeToolTip: false,
  showSeeMoreOptions: false,
  showShippingInfoInHover: false,
  showSimilarItem: false,
  showStoreInfo: false,
  showSSKUMiniSwatches: false,
  showQuantityBox: false,
  stfTopRight: false,
  storeId: '',
  swatchCount: 0,
  target: null,
  testId: null,
  titleMaxLine: null,
  zipCode: undefined,
  isBOLDCategory: false,
  showKPF: false,
  showSample: false,
  ignoreAnalytics: false,
  sharedSection: '',
  hideCompareBox: false,
};

export const getSimilarInfo = () => {
  // @todo this is bad. should be prop driven and consider the cookie as an extrnal global
  let searchCookie = '';
  if (typeof window !== 'undefined') {
    searchCookie = window.cookieUtils?.readBrowserCookie('x-ttsearch') || '';
  }
  const searchOptions = searchCookie.split(',');
  const showSimilarItemButton = searchOptions.filter((opt) => opt === 'OOS_button').length === 1;
  const showSimilarLinkInstock = searchOptions.filter((opt) => opt === 'INSTOCK_link').length === 1;
  return {
    showSimilarItemButton,
    showSimilarLinkInstock
  };
};

export const getProductPodContextValue = ({
  props,
  channel,
  state,
  hosts,
  productRef,
  productPodRef,
  handleProductPodExpansion,
  product,
  setState,
  setOverlayState
}) => {
  const {
    analyticsData,
    brandMaxLine,
    comparable,
    doNotShowChooseYourOptions,
    dpdNewTab,
    hasInStoreFilter,
    hideATC,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hidePromotions,
    hideRebates,
    hideSavingsText,
    hideSeeSimilarItems,
    hideShippingInfo,
    hideShopThisCollection,
    hideStoreInfo,
    hover,
    irgSectionName,
    itemId,
    maxKeyFeatures,
    mobilePodType,
    noATCFulfillment,
    onCartFailure,
    onCartSuccess,
    onClick,
    onCompare,
    onHoverSuperSku: onHoverSuperSkuProp,
    onFavoriteChange,
    onRemove,
    onSelectedForCompare,
    onShowSimilarClick,
    onSuperSkuChange,
    pageNumber,
    pageType,
    podCount,
    podIndex,
    podType,
    position,
    recommendationVersion,
    removable,
    selectedForCompare,
    showAisleAndBay,
    showDisabledATC,
    showFavoritesCount,
    showModelNumber,
    showPreferredPricingBadge,
    showPreferredPricingBadgeToolTip,
    showSeeMoreOptions,
    showShippingInfoInHover,
    showSimilarItem,
    showStoreInfo,
    showSSKUMiniSwatches,
    showQuantityBox,
    stfTopRight,
    storeId,
    swatchCount,
    target,
    titleMaxLine,
    isBOLDCategory,
    showKPF,
    showSample,
    ignoreAnalytics,
    sharedSection,
  } = props;

  const { showSimilarItemButton, showSimilarLinkInstock } = getSimilarInfo();
  const { isFulfillable } = podFulFillmentUtils;
  const { info } = product || {};
  const { isBuryProduct = false } = info || {};
  const hideSimilarItemLink = isFulfillable(product) || !isBuryProduct;

  const isHDQCSku = checkIsHDQCSku(product);
  const isScheduleAMeasureEligible = checkScheduleAMeasureEligibility(product);
  const isScheduleAConsultationEligible = isCustomKitchenCabinetProduct(product);
  const isRequestAQuoteEligible = isBrioProduct(product);

  const publishAnalytics = (eventName, action) => {
    publishAnalyticsEvent({
      eventName,
      action,
      analyticsData,
      itemId,
      productPodRef,
      ignoreAnalytics,
      pageType,
      podType,
      position,
      product,
      pageNumber,
      podCount,
      state,
      target,
      irgSectionName,
      recommendationVersion,
      setState
    });
  };

  const onSavingsCenterToggle = () => {
    setState({
      ...state,
      buyMoreSaveMore: !state.buyMoreSaveMore
    });
  };

  const onChangeSuperSku = (sskuData) => {

    if (onSuperSkuChange) {
      onSuperSkuChange(sskuData);
    }
    if (setOverlayState) {
      setOverlayState(false);
    }
    setState({
      ...state,
      selectedItemId: sskuData?.itemId,
      showExactPrice: true,
      isHovered: false,
      fetchPolicy: 'cache-first'
    });
  };

  const onHoverSuperSku = (sskuData) => {
    if (onHoverSuperSkuProp) {
      onHoverSuperSkuProp(sskuData);
    }
    setState({
      ...state,
      previousItemId: sskuData?.isHovered ? state.selectedItemId || itemId : null,
      selectedItemId: sskuData?.itemId,
      isHovered: sskuData?.isHovered,
      itemIdForSubComponents: state.selectedItemId || itemId,
      fetchPolicy: 'cache-first',
    });
  };

  const onProductLinkClick = (evt, opts) => {
    if (onClick) {
      onClick(evt, opts);
    }
  };

  const handleClick = (event, action) => {
    const customExperience = !!product?.info?.globalCustomConfigurator?.customExperience;
    if (!customExperience && onClick) {
      onClick(event, product, action);
    }

    publishAnalytics(PRODUCT_POD_CLICK, action);
  };

  const onQuantityChange = ({ quantity }) => {
    setState({
      ...state,
      quantity: Number(quantity)
    });
  };

  const chooseYourOptionsClick = (event) => {
    if (onClick) {
      onClick(event, product);
    }
  };

  return {
    analyticsData,
    brandMaxLine,
    buyMoreSaveMore: state?.buyMoreSaveMore,
    channel,
    chooseYourOptionsClick,
    comparable,
    doNotShowChooseYourOptions,
    hasInStoreFilter,
    hosts,
    hover,
    hideATC,
    hideFavorites,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hidePromotions,
    hideRebates,
    hideSavingsText,
    hideSeeSimilarItems,
    hideSimilarItemLink,
    hideShippingInfo,
    hideShopThisCollection,
    hideStoreInfo,
    itemId: productRef.current?.product?.itemId || state.itemIdForSubComponents,
    canonicalUrl: state.canonicalUrl,
    isHovered: state.isHovered,
    isHDQCSku,
    isRequestAQuoteEligible,
    isScheduleAMeasureEligible,
    isScheduleAConsultationEligible,
    previousItemId: state.previousItemId,
    onCompare,
    onCompareAnalytics: () => {
      publishAnalytics(PRODUCT_POD_COMPARE, COMPARE);
    },
    onSavingsCenterToggle,
    onSelectedForCompare,
    onHeaderClick: (event) => handleClick(event, PRODUCT_DETAILS),
    onImageClick: (event) => handleClick(event, PRODUCT_IMAGE),
    onRatingClick: (event) => handleClick(event, RATING),
    onCheckNearbyStoresClick: () => {
      publishAnalytics(PRODUCT_POD_CLICK, CHECK_NEARBY_STORE);
    },
    onFavoriteClick: () => {},
    onSimilarItemsClick: (similarButtonEnabled) => {
      const path = getDomPath(productPodRef, { attributes: ['id'] });
      publish(PRODUCT_POD_SIMILAR, { path, product });
      handleProductPodExpansion(itemId, position, productPodRef, similarButtonEnabled);
    },
    dpdNewTab,
    onProductLinkClick,
    onShowSimilarClick,
    onChangeSuperSku,
    onHoverSuperSku,
    onFavoriteChange,
    onCartSuccess,
    onCartFailure,
    onQuantityChange,
    onRemove,
    quantity: state.quantity,
    maxKeyFeatures,
    mobilePodType,
    noATCFulfillment,
    pageType,
    podType,
    podIndex,
    position,
    product,
    productPodRef,
    publishAnalytics,
    removable,
    selectedForCompare,
    selectedItemId: state.selectedItemId,
    showAisleAndBay,
    showModelNumber,
    showPreferredPricingBadge,
    showPreferredPricingBadgeToolTip,
    showStoreInfo,
    showExactPrice: state.showExactPrice,
    showSeeMoreOptions,
    showShippingInfoInHover,
    showSimilarItem,
    showSSKUMiniSwatches,
    showQuantityBox,
    target,
    titleMaxLine,
    showFavoritesCount,
    stfTopRight,
    storeId,
    showDisabledATC,
    swatchCount,
    isBOLDCategory,
    showKPF,
    showSample,
    sharedSection,
    showSimilarItemButton,
    showSimilarLinkInstock
  };
};
